import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, RotateCcw, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { LOGO } from "@/lib/constants";
import { assistantWaLink } from "@/lib/whatsapp";

const newSession = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const detectLang = (text) => (/[\u0590-\u05FF]/.test(text) ? "he" : null);

export default function Assistant({ enabled = true, contact }) {
  const { t, lang, isRTL } = useLang();
  const a = t.assistant;
  const [open, setOpen] = useState(false);
  const [chatLang, setChatLang] = useState(lang);
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const sessionRef = useRef(newSession());
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const phone = contact?.phone_intl || "972555004933";
  const ca = t.assistant;

  // follow site language while no messages yet
  useEffect(() => { if (messages.length === 0) setChatLang(lang); }, [lang, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, showFeedback]);

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  if (!enabled) return null;

  const send = async (text) => {
    const clean = (text || "").trim();
    if (!clean || busy) return;
    const detected = detectLang(clean) || chatLang;
    if (detected !== chatLang) setChatLang(detected);
    const isNew = messages.length === 0;
    const nextMsgs = [...messages, { role: "user", content: clean }];
    setMessages(nextMsgs);
    setInput("");
    setShowFeedback(false);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/message", {
        session_id: sessionRef.current,
        lang: detected,
        is_new: isNew,
        messages: nextMsgs.map((m) => ({ role: m.role, content: m.content })),
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      if (nextMsgs.length >= 3) setShowFeedback(true);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: ca.error || ca.disclaimer }]);
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setMessages([]);
    setShowFeedback(false);
    sessionRef.current = newSession();
    setChatLang(lang);
  };

  const openWhatsApp = () => {
    api.post("/chat/event", { event: "whatsapp_click" }).catch(() => {});
    window.open(assistantWaLink(phone, chatLang), "_blank", "noopener");
  };

  const feedback = (ok) => {
    api.post("/chat/event", { event: ok ? "feedback_yes" : "feedback_no" }).catch(() => {});
    setShowFeedback(false);
    if (!ok) openWhatsApp();
  };

  const dir = chatLang === "he" ? "rtl" : "ltr";

  return (
    <>
      {/* Launcher (stacked above the WhatsApp button) */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            whileHover={{ scale: 1.04 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-[5.25rem] end-5 z-40 flex items-center gap-2 rounded-full bg-ivory border border-gold/50 ps-3 pe-4 py-2.5 text-taupe shadow-[0_8px_30px_rgba(200,155,90,0.28)] hover:bg-cream transition-colors"
            data-testid="assistant-launcher"
            aria-label={a.launcher}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold text-white">
              <MessageCircle className="h-4 w-4" />
            </span>
            <span className="text-sm font-medium">{a.launcher}</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            dir={dir}
            className="fixed z-50 bg-ivory border border-sand shadow-2xl flex flex-col
                       inset-x-3 bottom-3 top-16 rounded-3xl
                       sm:inset-auto sm:bottom-5 sm:end-5 sm:top-auto sm:w-[390px] sm:h-[600px]"
            data-testid="assistant-window"
            role="dialog"
            aria-label={a.name}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-sand bg-cream rounded-t-3xl">
              <img src={LOGO} alt="" className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/40" />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-base text-ink leading-tight truncate">{a.name}</p>
                <p className="text-xs text-gold flex items-center gap-1"><Sparkles className="h-3 w-3" /> {a.status}</p>
              </div>
              <button onClick={restart} title={a.restart} className="p-2 text-taupe hover:text-gold transition-colors" data-testid="assistant-restart" aria-label={a.restart}>
                <RotateCcw className="h-4 w-4" />
              </button>
              <button onClick={() => setOpen(false)} title={a.close} className="p-2 text-taupe hover:text-ink transition-colors" data-testid="assistant-close" aria-label={a.close}>
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="assistant-messages">
              <Bubble role="assistant" text={a.greeting} />

              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="assistant-quick">
                  {a.quick.map((q, i) => (
                    <button key={i} onClick={() => send(q)} data-testid={`assistant-quick-${i}`}
                      className="rounded-full border border-sand bg-white px-3 py-1.5 text-xs text-taupe hover:border-gold hover:text-gold transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.content} />)}

              {busy && (
                <div className="flex items-center gap-1.5 text-taupe/70 text-sm ps-1" data-testid="assistant-typing">
                  <span className="h-2 w-2 rounded-full bg-gold/70 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 rounded-full bg-gold/70 animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="h-2 w-2 rounded-full bg-gold/70 animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              )}

              {showFeedback && !busy && (
                <div className="rounded-2xl bg-white border border-sand px-4 py-3" data-testid="assistant-feedback">
                  <p className="text-sm text-ink mb-2">{a.feedbackQ}</p>
                  <div className="flex gap-2">
                    <button onClick={() => feedback(true)} className="rounded-full border border-sand px-4 py-1.5 text-xs text-taupe hover:border-gold hover:text-gold transition-colors" data-testid="feedback-yes">{a.yes}</button>
                    <button onClick={() => feedback(false)} className="rounded-full border border-sand px-4 py-1.5 text-xs text-taupe hover:border-gold hover:text-gold transition-colors" data-testid="feedback-no">{a.no}</button>
                  </div>
                </div>
              )}
            </div>

            {/* WhatsApp quick access */}
            <button onClick={openWhatsApp} className="mx-4 mb-2 flex items-center justify-center gap-2 rounded-full bg-whatsapp py-2.5 text-sm font-medium text-white hover:brightness-105 transition-[filter]" data-testid="assistant-whatsapp">
              <MessageCircle className="h-4 w-4" /> {a.whatsapp}
            </button>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(input); }}
              className="flex items-center gap-2 border-t border-sand p-3"
            >
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={a.placeholder}
                dir={dir}
                className="flex-1 rounded-full border border-sand bg-white px-4 py-2.5 text-sm text-ink outline-none focus:border-gold"
                data-testid="assistant-input"
                aria-label={a.placeholder}
              />
              <button type="submit" disabled={busy || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold text-white disabled:opacity-50 hover:brightness-105 transition-[filter]"
                data-testid="assistant-send" aria-label={a.send}>
                <Send className="h-4 w-4 rtl:-scale-x-100" />
              </button>
            </form>
            <p className="px-4 pb-3 text-[11px] text-taupe/60 text-center">{a.disclaimer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Bubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] whitespace-pre-wrap text-sm leading-relaxed px-4 py-2.5 rounded-2xl ${
          isUser ? "bg-gold text-white rounded-ee-sm" : "bg-white border border-sand text-ink rounded-es-sm"
        }`}
        data-testid={`bubble-${role}`}
      >
        {text}
      </div>
    </div>
  );
}
